import { Injectable } from '@angular/core';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  register,
  onRegistered,
  unregister,
  onUnregistered,
  Messaging,
  onMessage
} from 'firebase/messaging';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {

  private app: FirebaseApp;
  private messaging: Messaging;

  constructor() {
    if (!getApps().length) {
      this.app = initializeApp(environment.firebaseConfig);
    } else {
      this.app = getApps()[0];
    }
    this.messaging = getMessaging(this.app);
  }

  /**
   * Request notification permission, register with FCM via FID, and return the FID.
   * Returns null if permission is denied or VAPID key is not configured.
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    if (!environment.firebaseConfig.vapidKey) {
      console.warn('FirebaseMessagingService: vapidKey not configured in environment. Push notifications disabled.');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('FirebaseMessagingService: Notification permission denied.');
        return null;
      }

      const swRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

      return new Promise<string | null>((resolve) => {
        const unsubscribe = onRegistered(this.messaging, (fid) => {
          unsubscribe();
          resolve(fid);
        });

        register(this.messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
          serviceWorkerRegistration: swRegistration
        }).catch((err) => {
          console.error('FirebaseMessagingService: Failed to register for push', err);
          unsubscribe();
          resolve(null);
        });
      });
    } catch (err) {
      console.error('FirebaseMessagingService: Failed to get FCM token', err);
      return null;
    }
  }

  /**
   * Unregister this instance from FCM (call on logout).
   * Triggers onUnregistered internally.
   */
  async deleteToken(): Promise<void> {
    try {
      await unregister(this.messaging);
    } catch (err) {
      console.error('FirebaseMessagingService: Failed to unregister from FCM', err);
    }
  }

  /**
   * Listen for foreground messages (when app is open).
   * Pass a callback to handle the notification display.
   */
  onForegroundMessage(handler: (payload: any) => void) {
    return onMessage(this.messaging, handler);
  }
}

